import type {
  VideoScreenInsightRow,
  VideoScreenInsightSection,
  VideoScreenInsights,
  VideoScreenLeadershipBriefBlock,
  VideoScreenLeadershipBriefItem,
  VideoScreenLeadershipBriefSection,
  VideoScreenLeadershipMetric,
  VideoScreenProtocolRisk,
} from '../../../services/api.service';
import {
  formatCellValue,
  isEmptyCell,
  isLabelColumn,
  isTotalRow,
  parseNumericValue,
  pickDeviationColumn,
  sectionAnchorId,
} from './video-insights-utils';

export type ParsedActionItem = { text: string; assignee: string };

export type RiskAttribution = {
  kind: 'speaker' | 'recording' | 'unknown';
  label: string;
  speaker?: string;
  timestamp?: string;
};

export type LeadershipRisk = {
  id: string;
  sectionId: string;
  sectionTitle: string;
  rowLabel: string;
  entityLabel: string;
  deviationDisplay: string;
  deviationScore: number;
  /** Сырой текст причины (для экспорта) */
  reason: string;
  /** Заголовок карточки риска */
  headline: string;
  /** Источник: спикер в записи, таблица или не уточнено */
  attribution: RiskAttribution;
  yearForecast?: string;
  previousMeetingNote: string;
  actionItem: ParsedActionItem | null;
  /** Строка поручения для карточки (если нет actionItem из протокола) */
  pendingAssignment?: string;
};

export type LeadershipMetric = VideoScreenLeadershipMetric;
export type ProtocolRisk = VideoScreenProtocolRisk;
export type LeadershipBriefSection = VideoScreenLeadershipBriefSection;
export type LeadershipBriefItem = VideoScreenLeadershipBriefItem;
export type LeadershipBriefBlock = VideoScreenLeadershipBriefBlock;

const UNKNOWN_REASON_RE = /не озвучена|не детализирована|не уточн/i;

const PREVIOUS_MEETING_DEFAULT = 'нет данных';

const REASON_HINTS: { pattern: RegExp; reason: string }[] = [
  {
    pattern: /starlink|старлинк/i,
    reason: 'Ирина Сурьма (3:50): в представленных материалах не отражены показатели по направлению Starlink «разведчики» — требуется актуализация данных и пересмотр подхода к планированию',
  },
  {
    pattern: /расход|тлк/i,
    reason: 'Ирина Сурьма (7:43): расходы ТЛК «в тени», нужен отдельный анализ',
  },
  {
    pattern: /шымкент/i,
    reason: 'В записи: срез ~800 млн по Шымкенту, переговоры с РЦКС',
  },
  {
    pattern: /спутник/i,
    reason: 'Спутниковая связь B2B — отставание 50% плана, причина в записи не детализирована',
  },
];

const ACTION_LINK_HINTS: { risk: RegExp; task: RegExp }[] = [
  { risk: /starlink|старлинк|спутник/i, task: /starlink|спутник|старлинк/i },
  { risk: /формул|excel|ожидаем|вызов/i, task: /формул|excel|ожидаем|вызов/i },
  { risk: /актуализ|16:00/i, task: /актуализ|16:00/i },
  { risk: /презентац|доход.*b2o|бито/i, task: /презентац|доход.*b2o|бито/i },
  { risk: /клиент|распредел/i, task: /клиент|распредел/i },
  { risk: /начислен|прогноз/i, task: /начислен|прогноз/i },
  { risk: /служебк|куандык/i, task: /служебк|куандык/i },
];

export function parseActionItems(raw: string[]): ParsedActionItem[] {
  return raw
    .map(line => {
      const text = (line || '').trim();
      if (!text) return null;
      const sep = text.lastIndexOf(' — ');
      if (sep > 0) {
        return { text: text.slice(0, sep).trim(), assignee: text.slice(sep + 3).trim() };
      }
      return { text, assignee: '' };
    })
    .filter((x): x is ParsedActionItem => x !== null);
}

function labelColumn(columns: string[]): string | null {
  return columns.find(isLabelColumn) || columns[0] || null;
}

function findAmountColumns(columns: string[], labelCol: string, deviationCol: string | null) {
  const plan5 = columns.find(c => /план\s*5/i.test(c));
  const fact = columns.find(c => /^факт$/i.test(c.trim()) || /факт\s*5/i.test(c));
  const planAnnual = columns.find(c => /план\s*2026/i.test(c));
  const factAnnual = columns.find(c => /факт\s*2026|ожид/i.test(c));
  return { plan5, fact, planAnnual, factAnnual, labelCol, deviationCol };
}

function deviationScore(deviationDisplay: string, column: string): number {
  const n = parseNumericValue(deviationDisplay);
  if (n === null) return 0;
  if (/отклонение/i.test(column)) return Math.abs(n);
  if (/%/.test(deviationDisplay) || /% плана/i.test(column)) {
    if (n < 100) return Math.abs(100 - n) * 10_000;
    return 0;
  }
  if (n < 0) return Math.abs(n);
  return 0;
}

function isProblemDeviation(display: string, column: string): boolean {
  const n = parseNumericValue(display);
  if (n === null) return /^[-−]/.test(display.trim());
  if (/отклонение/i.test(column)) return n < 0;
  if (/% плана/i.test(column) || /%$/.test(column.trim())) return n < 100;
  return n < 0;
}

function buildYearForecast(
  section: VideoScreenInsightSection,
  row: VideoScreenInsightRow,
  columns: string[],
): string | undefined {
  const title = section.title.toLowerCase();
  if (!title.includes('5 мес') && !title.includes('5 месяц')) return undefined;

  const labelCol = labelColumn(columns);
  const deviationCol = pickDeviationColumn(columns);
  if (!labelCol) return undefined;

  const { plan5, fact } = findAmountColumns(columns, labelCol, deviationCol);
  if (!plan5 || !fact) return undefined;

  const planVal = parseNumericValue(formatCellValue(row[plan5]));
  const factVal = parseNumericValue(formatCellValue(row[fact]));
  if (planVal === null || factVal === null || planVal <= 0) return undefined;

  const pctOfPlan = Math.round((factVal / planVal) * 100);
  return `прогноз к году ~${pctOfPlan}%`;
}

function resolveReason(
  sectionTitle: string,
  rowLabel: string,
  hints?: { match?: string; section?: string; reason?: string }[],
): string {
  const combined = `${sectionTitle} ${rowLabel}`;
  if (hints?.length) {
    const hit = hints.find(h => {
      const sectionOk = !h.section || sectionTitle.toLowerCase().includes(h.section.toLowerCase());
      const matchOk = !h.match || combined.toLowerCase().includes(h.match.toLowerCase());
      return sectionOk && matchOk && !!h.reason;
    });
    if (hit?.reason) return hit.reason;
  }

  for (const hint of REASON_HINTS) {
    if (hint.pattern.test(combined)) return hint.reason;
  }
  return 'причина не озвучена в записи';
}

function entityFromRowLabel(rowLabel: string): string {
  const trimmed = rowLabel.trim();
  const paren = trimmed.indexOf(' (');
  return (paren > 0 ? trimmed.slice(0, paren) : trimmed).trim() || trimmed;
}

export function parseRiskPresentation(
  reason: string,
  rowLabel: string,
  deviationDisplay: string,
): { headline: string; attribution: RiskAttribution } {
  const speakerMatch = reason.match(/^(.+?)\s*\((\d{1,2}:\d{2})\)\s*:\s*(.+)$/);
  if (speakerMatch) {
    const [, speaker, timestamp, note] = speakerMatch;
    const sp = speaker.trim();
    return {
      headline: note.trim(),
      attribution: {
        kind: 'speaker',
        label: `${sp} · ${timestamp}`,
        speaker: sp,
        timestamp,
      },
    };
  }

  const recordingMatch = reason.match(/^В записи:\s*(.+)$/i);
  if (recordingMatch) {
    return {
      headline: recordingMatch[1].trim(),
      attribution: { kind: 'recording', label: 'По записи совещания' },
    };
  }

  if (UNKNOWN_REASON_RE.test(reason)) {
    const entity = entityFromRowLabel(rowLabel);
    return {
      headline: `${entity}: отклонение ${deviationDisplay} к плану`,
      attribution: { kind: 'unknown', label: 'Причина в записи не уточнена' },
    };
  }

  return {
    headline: reason,
    attribution: { kind: 'recording', label: 'По данным таблицы на экране' },
  };
}

function matchActionItem(
  sectionTitle: string,
  rowLabel: string,
  actionItems: ParsedActionItem[],
): ParsedActionItem | null {
  const combined = `${sectionTitle} ${rowLabel}`.toLowerCase();
  for (const link of ACTION_LINK_HINTS) {
    if (!link.risk.test(combined)) continue;
    const hit = actionItems.find(item => link.task.test(item.text.toLowerCase()));
    if (hit) return hit;
  }
  return null;
}

export function getLeadershipBriefSections(
  leadership?: VideoScreenInsights['leadership'],
): LeadershipBriefSection[] {
  return leadership?.brief_sections?.length ? leadership.brief_sections : [];
}

export function getLeadershipBriefBlocks(
  leadership?: VideoScreenInsights['leadership'],
): LeadershipBriefBlock[] {
  return leadership?.brief_blocks?.length ? leadership.brief_blocks : [];
}

export function getLeadershipBriefSummary(
  leadership?: VideoScreenInsights['leadership'],
): string {
  return leadership?.brief_summary?.trim() || '';
}

export function getLeadershipBriefTitle(
  leadership?: VideoScreenInsights['leadership'],
): string {
  return leadership?.brief_title?.trim() || 'Краткий аналитический бриф для руководства';
}

export function formatBriefSectionsForCopy(
  sections: LeadershipBriefSection[],
  title = 'Краткий аналитический бриф для руководства',
): string {
  if (!sections.length) return '';
  const lines = [title + ':', ''];
  for (const section of sections) {
    lines.push(`${section.label}: ${section.text}`);
  }
  return lines.join('\n');
}

export function formatBriefBlocksForCopy(
  blocks: LeadershipBriefBlock[],
  title = 'Краткий аналитический бриф для руководства',
  summary = '',
): string {
  if (!blocks.length) return '';
  const lines = [title, ''];
  for (const block of blocks) {
    const heading = block.number
      ? `${block.number}. ${block.title}${block.subtitle ? ` (${block.subtitle})` : ''}`
      : block.title;
    lines.push(heading);
    for (const item of block.items) {
      if (item.label) lines.push(`${item.label}: ${item.text}`);
      else lines.push(item.text);
      for (const bullet of item.bullets || []) {
        lines.push(`  • ${bullet}`);
      }
      if (item.action) lines.push(`  → ${item.action}`);
    }
    lines.push('');
  }
  if (summary) {
    lines.push('Резюме для СД / Руководства:', summary);
  }
  return lines.join('\n').trim();
}

const BRIEF_HIGHLIGHT_RE = /(\(\d+%\)|\(\−\d+%\)|\d[\d\s]+\s*тыс\.?\s*тг|−\d[\d\s]*\s*тыс\.?\s*тг|\d+[,.]?\d*\s*(?:млрд|млн)\s*(?:тенге|теңге)|−\d+%|−\d+[,.]?\d*\s*%|\+\d+%|~?\d+%|×[\d,]+)/g;

export function splitBriefHighlights(text: string): string[] {
  return text.split(BRIEF_HIGHLIGHT_RE).filter(Boolean);
}

export function isBriefHighlight(part: string): boolean {
  return /^(\(\d+%\)|\(\−\d+%\)|\d[\d\s]+\s*тыс\.?\s*тг|−\d[\d\s]*\s*тыс\.?\s*тг|\d+[,.]?\d*\s*(?:млрд|млн)\s*(?:тенге|теңге)|−\d+%|−\d+[,.]?\d*\s*%|\+\d+%|~?\d+%|×[\d,]+)$/.test(part);
}

export function getLeadershipMetrics(
  leadership?: VideoScreenInsights['leadership'],
): LeadershipMetric[] {
  return leadership?.metrics?.length ? leadership.metrics : [];
}

export function resolveMetricTone(
  metric: LeadershipMetric,
): 'positive' | 'negative' | 'neutral' {
  if (metric.tone === 'positive' || metric.tone === 'negative') {
    return metric.tone;
  }
  const value = (metric.value || '').trim();
  if (/^[+＋]/.test(value)) return 'positive';
  if (/^[−\-–]/.test(value)) return 'negative';
  return 'neutral';
}

export type LeadershipMetricZone = 'good' | 'medium' | 'bad';

function parseMetricNumber(value: string): number | null {
  return parseNumericValue((value || '').replace(/^[~≈]/, ''));
}

/** Пороговая зона «хорошо / средне / плохо» для карточки scorecard-метрики. */
export function resolveMetricZone(metric: LeadershipMetric): LeadershipMetricZone {
  const tone = resolveMetricTone(metric);
  if (tone === 'positive') return 'good';
  if (tone === 'negative') return 'bad';

  const n = parseMetricNumber(metric.value);
  if (n === null) return 'medium';
  if (/%/.test(metric.value)) {
    if (n >= 100) return 'good';
    if (n >= 90) return 'medium';
    return 'bad';
  }
  return 'medium';
}

/** Одна строка авто-инсайта под числом метрики. */
export function buildMetricInsight(
  metric: LeadershipMetric,
  zone: LeadershipMetricZone,
): string {
  const n = parseMetricNumber(metric.value);
  const isPct = /%/.test(metric.value || '');

  if (zone === 'good') {
    if (n !== null && isPct && n >= 100) {
      return n > 100 ? `план перевыполнен на ${Math.round(n - 100)}%` : 'план выполнен полностью';
    }
    if (/^[+＋]/.test((metric.value || '').trim())) return 'положительная динамика';
    return 'в пределах нормы';
  }

  if (zone === 'bad') {
    if (n !== null && n < 0) return `отставание от плана ${Math.abs(Math.round(n))}%`;
    if (n !== null && isPct && n < 100) return `исполнение ${Math.round(n)}% — зона риска`;
    return 'требует внимания руководства';
  }

  return 'пограничное значение — на контроле';
}

export function getProtocolRisks(
  leadership?: VideoScreenInsights['leadership'],
): ProtocolRisk[] {
  return leadership?.protocol_risks?.length ? leadership.protocol_risks : [];
}

export function resolvePendingAssignment(
  risk: LeadershipRisk,
  leadership?: VideoScreenInsights['leadership'],
): string {
  if (risk.actionItem?.assignee) {
    return `${risk.actionItem.assignee} — ${risk.actionItem.text}`;
  }

  const cfg = leadership?.pending_assignments;
  if (!cfg) return '';

  const meetingAt = cfg.meeting_at || '21.06.2026, 16:00';
  const note = cfg.note || 'поручение не зафиксировано — вынести на';
  const combined = `${risk.sectionTitle} ${risk.entityLabel} ${risk.rowLabel}`.toLowerCase();

  let assignees = cfg.default_assignees || 'Жансерик-Козлова И., Сурьма И.В.';
  for (const override of cfg.overrides || []) {
    if (override.match && new RegExp(override.match, 'i').test(combined)) {
      assignees = override.assignees || assignees;
      break;
    }
  }

  return `${assignees} — ${note} ${meetingAt}`;
}

export function enrichRisksWithAssignments(
  risks: LeadershipRisk[],
  leadership?: VideoScreenInsights['leadership'],
): LeadershipRisk[] {
  return risks.map(risk => ({
    ...risk,
    pendingAssignment: resolvePendingAssignment(risk, leadership),
  }));
}

export function formatProtocolRiskAssignment(risk: ProtocolRisk): string {
  if (risk.deadline) {
    return `${risk.assignee} — срок: ${risk.deadline}`;
  }
  if (risk.status_badge === 'на паузе') {
    return `${risk.assignee} — до поступления новых вводных`;
  }
  return risk.assignee;
}

export function extractTopRisks(
  sections: VideoScreenInsightSection[],
  actionItems: ParsedActionItem[],
  leadership?: VideoScreenInsights['leadership'],
  limit = 5,
): LeadershipRisk[] {
  const risks: LeadershipRisk[] = [];

  sections.forEach((section, sIdx) => {
    const columns = section.columns || [];
    const rows = section.rows || [];
    const labelCol = labelColumn(columns);
    const deviationCol = pickDeviationColumn(columns);
    if (!labelCol || !deviationCol) return;

    const sectionId = sectionAnchorId(sIdx, section.title);

    rows.forEach((row, rIdx) => {
      const rowLabel = formatCellValue(row[labelCol]);
      if (isEmptyCell(rowLabel)) return;

      const deviationDisplay = formatCellValue(row[deviationCol]);
      if (isEmptyCell(deviationDisplay) || !isProblemDeviation(deviationDisplay, deviationCol)) {
        return;
      }

      const score = deviationScore(deviationDisplay, deviationCol);
      if (score <= 0) return;

      const isAggregate = isTotalRow(row, columns)
        || /^(итого|всего|total)$/i.test(rowLabel);

      if (!isAggregate && score < 5_000 && /%/.test(deviationDisplay)) {
        const pct = parseNumericValue(deviationDisplay);
        if (pct !== null && pct >= 100) return;
      }

      const actionItem = matchActionItem(section.title, rowLabel, actionItems);
      const yearForecast = buildYearForecast(section, row, columns);
      const displayRowLabel = isAggregate ? section.title : `${rowLabel} (${section.title})`;
      const reason = resolveReason(section.title, rowLabel, leadership?.risk_hints);
      const { headline, attribution } = parseRiskPresentation(
        reason,
        displayRowLabel,
        deviationDisplay,
      );

      risks.push({
        id: `${sectionId}-${rIdx}`,
        sectionId,
        sectionTitle: section.title,
        rowLabel: displayRowLabel,
        entityLabel: isAggregate ? section.title : entityFromRowLabel(rowLabel),
        deviationDisplay,
        deviationScore: score + (isAggregate ? 50_000 : 0),
        reason,
        headline,
        attribution,
        yearForecast,
        previousMeetingNote: leadership?.previous_meeting_note || PREVIOUS_MEETING_DEFAULT,
        actionItem,
      });
    });
  });

  return risks
    .sort((a, b) => b.deviationScore - a.deviationScore)
    .slice(0, limit);
}

export function buildLeadershipBrief(
  sections: VideoScreenInsightSection[],
  topRisks: LeadershipRisk[],
  leadership?: VideoScreenInsights['leadership'],
): string {
  if (leadership?.brief?.trim()) return leadership.brief.trim();

  if (!sections.length && topRisks.length === 0) {
    return '';
  }

  const directionSection = sections.find(s => /направлен/i.test(s.title));
  const totalRow = directionSection?.rows?.find(row => {
    const label = formatCellValue(row[directionSection.columns?.[0] || '']);
    return /всего|итого/i.test(label);
  });
  const totalPct = totalRow
    ? formatCellValue(totalRow['% плана 2026'] || totalRow['% плана'] || '')
    : '';

  const starlink = topRisks.find(r => /starlink/i.test(r.sectionTitle));
  const expenses = topRisks.find(r => /расход/i.test(r.sectionTitle));
  const b2o = topRisks.find(r => /b2o/i.test(r.rowLabel) && !/b2b/i.test(r.rowLabel));

  const chunks: string[] = [];

  if (totalPct && totalPct !== '—') {
    const pct = totalPct.includes('%') ? totalPct : `${totalPct}%`;
    chunks.push(
      `Консолидированная выручка компании — ${pct} к плану; положительное отклонение сформировано преимущественно сегментом B2B`,
    );
  } else {
    chunks.push(
      'Консолидированная выручка компании превышает плановый показатель за счёт сегмента B2B',
    );
  }

  if (starlink) {
    chunks.push(
      starlink.yearForecast
        ? `прогноз исполнения годового плана по направлению Starlink — ${starlink.yearForecast.replace('прогноз к году ~', 'около ').replace('прогноз к году ', '')}`
        : `направление Starlink отстаёт от плана (${starlink.deviationDisplay})`,
    );
  }

  if (expenses) {
    const unexplained =
      expenses.attribution.kind === 'unknown' || expenses.reason.includes('в тени');
    chunks.push(
      unexplained
        ? `расходы ТЛК ниже плана на ${expenses.deviationDisplay.replace(/^[−\-–]/, '')} без объяснённой причины`
        : `расходы ТЛК — отклонение от плана ${expenses.deviationDisplay}`,
    );
  } else if (b2o) {
    chunks.push(`сегмент B2O — ${b2o.deviationDisplay} к плану`);
  }

  if (!chunks.length) {
    return 'Ключевые отклонения по таблицам совещания — см. топ-риски ниже.';
  }

  return `${chunks.join('. ')}.`;
}
